import type { AtlasWorkspace } from './types';
import { validateWorkspace } from './workspace-validation';

const DATABASE = 'inventivehq-dependency-atlas';
const STORE = 'workspaces';
const META_STORE = 'metadata';
const ACTIVE_WORKSPACE_KEY = 'activeWorkspaceId';
const FALLBACK_KEY = 'inventivehq:dependency-atlas:workspace';

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE, 2);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'id' });
      if (!db.objectStoreNames.contains(META_STORE)) db.createObjectStore(META_STORE, { keyPath: 'key' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}
export async function loadWorkspace(id: string): Promise<AtlasWorkspace | null> {
  try {
    const db = await openDatabase();
    return await new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE, META_STORE], 'readonly');
      const metadataRequest = transaction.objectStore(META_STORE).get(ACTIVE_WORKSPACE_KEY);
      metadataRequest.onerror = () => reject(metadataRequest.error);
      metadataRequest.onsuccess = () => {
        const activeId = (metadataRequest.result as { value?: string } | undefined)?.value || id;
        const workspaceRequest = transaction.objectStore(STORE).get(activeId);
        workspaceRequest.onsuccess = () => resolve(validateWorkspace(workspaceRequest.result).workspace);
        workspaceRequest.onerror = () => reject(workspaceRequest.error);
      };
    });
  } catch {
    try {
      const raw = localStorage.getItem(FALLBACK_KEY);
      return raw ? validateWorkspace(JSON.parse(raw)).workspace : null;
    } catch {
      return null;
    }
  }
}

export async function saveWorkspace(workspace: AtlasWorkspace): Promise<void> {
  const updated = { ...workspace, updatedAt: new Date().toISOString() };
  try {
    const db = await openDatabase();
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction([STORE, META_STORE], 'readwrite');
      transaction.objectStore(STORE).put(updated);
      transaction.objectStore(META_STORE).put({ key: ACTIVE_WORKSPACE_KEY, value: updated.id });
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  } catch {
    localStorage.setItem(FALLBACK_KEY, JSON.stringify(updated));
  }
}
